import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { SocketMessage } from 'services/api/types';
import getSocket from '.';
import { WS_ROS_ACTIVE_CHANGED, WS_ROS_VERSION_GENERATED } from 'constants/actionTypes';
import { reportOfSaleApiSlice, ReportOfSaleVersion } from 'services/api/report-of-sale';
import uploadVehicleApi from 'services/api/upload-vehicle';

const ROS_FIELD_MAP: Record<'admin' | 'buyer' | 'seller', string> = {
  admin: 'admin_report_of_sale',
  buyer: 'buyer_report_of_sale',
  seller: 'seller_report_of_sale',
};

export const useAdminGlobalSocketHandler = () => {
  const user = useSelector((state: any) => state.user?.user);
  const dispatch = useDispatch() as ThunkDispatch<any, any, AnyAction>;

  const listener = ({ type, payload }: SocketMessage) => {
    if (type === WS_ROS_VERSION_GENERATED) {
      const vehicleId: number = payload.vehicle_id;
      const roleKey = payload.report_type as 'admin' | 'buyer' | 'seller';

      dispatch(
        reportOfSaleApiSlice.util.updateQueryData('getReportOfSaleVersions', { vehicleId }, draft => {
          const existing = draft[roleKey] ?? [];
          const isActive = !!payload.is_active;
          // System-generated versions arrive active — keep only one active per type.
          if (isActive) existing.forEach(v => (v.is_active = false));
          const newEntry: ReportOfSaleVersion = {
            id: payload.id,
            version: payload.version,
            url: payload.url,
            is_active: isActive,
            actor: payload.actor,
            action: existing.length === 0 ? 'Created' : 'Modified',
            created_at: payload.created_at,
          };
          draft[roleKey] = [newEntry, ...existing];
        })
      );

      // Only the active version is reflected on the vehicle object.
      if (payload.is_active) {
        const field = ROS_FIELD_MAP[roleKey];
        dispatch(
          uploadVehicleApi.util.updateQueryData('getVehicleById', vehicleId, draft => {
            (draft as any)[field] = payload.url;
          })
        );
      }
    }

    if (type === WS_ROS_ACTIVE_CHANGED) {
      const vehicleId: number = payload.vehicle_id;
      const roleKey = payload.report_type as 'admin' | 'buyer' | 'seller';

      dispatch(
        reportOfSaleApiSlice.util.updateQueryData('getReportOfSaleVersions', { vehicleId }, draft => {
          const list = draft[roleKey];
          if (list) list.forEach(v => (v.is_active = v.version === payload.version));
        })
      );

      const field = ROS_FIELD_MAP[roleKey];
      dispatch(
        uploadVehicleApi.util.updateQueryData('getVehicleById', vehicleId, draft => {
          (draft as any)[field] = payload.url;
        })
      );
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') return;

    const socket = getSocket();
    socket.emit('subscribe', 'admins');
    socket.on('message', listener);

    return () => {
      socket.emit('unsubscribe', 'admins');
      socket.off('message', listener);
    };
  }, [dispatch, user?.id, user?.role]);
};
